export const client = async (endpoint, { body, ...customConfig } = {}) => {
    const headers = { 'Content-Type': 'application/json' };

    // JWT Token from localStorage
    const token = localStorage.getItem('access_token');
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // CSRF Token for Django (kept for session-auth fallback)
    const getCookie = (name) => {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    }
    const csrftoken = getCookie('csrftoken');
    if (csrftoken) {
        headers['X-CSRFToken'] = csrftoken;
    }

    const config = {
        method: customConfig.method || (body ? 'POST' : 'GET'),
        credentials: 'include',
        ...customConfig,
        headers: {
            ...headers,
            ...customConfig.headers,
        },
    }

    if (body) {
        if (body instanceof FormData) {
            config.body = body;
            // Fetch will automatically set the correct Content-Type with boundary for FormData
            delete config.headers['Content-Type'];
        } else {
            config.body = JSON.stringify(body);
        }
    }

    // Prefix with backend host if not absolute
    let url = endpoint;
    const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? window.location.origin : 'https://sgrloto.com';
    if (!endpoint.startsWith('http') && !endpoint.startsWith('/')) {
        url = `${API_BASE}/api/${endpoint}`;
    } else if (endpoint.startsWith('/')) {
        url = `${API_BASE}${endpoint}`;
    }

    try {
        const response = await fetch(url, config);
        
        if (response.ok) {
            // Handle 204 No Content or empty responses
            if (response.status === 204) return null;
            
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                return await response.json();
            }
            return null;
        }
        if (response.status === 429) {
            return Promise.reject(await response.json().catch(() => ({
                status: 429,
                detail: 'Trop de requêtes simultanées. Réessayez dans un instant.',
            })));
        }
        // Handle 401 — try token refresh
        if (response.status === 401) {
            // Prevent infinite loops or attempting to refresh on endpoints that handle auth
            if (url.includes('/login/') || url.includes('/logout/') || url.includes('/auth/refresh/')) {
                return Promise.reject(await response.json().catch(() => ({ status: 401, message: 'Non autorisé' })));
            }

            console.warn(`[API Client] 401 Unauthorized for ${url}. Attempting token refresh...`);
            const refreshed = await tryRefreshToken();
            if (refreshed) {
                console.info(`[API Client] Token refreshed. Retrying ${url}...`);
                // Retry with new token
                config.headers['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
                const retryResponse = await fetch(url, config);
                if (retryResponse.ok) {
                    if (retryResponse.status === 204) return null;
                    const contentType = retryResponse.headers.get("content-type");
                    if (contentType && contentType.includes("application/json")) {
                        return await retryResponse.json();
                    }
                    return null;
                }
                console.error(`[API Client] Retry failed for ${url} with status ${retryResponse.status}`);
            } else {
                console.error(`[API Client] Token refresh failed for ${url}. Logging out.`);
            }
            window.dispatchEvent(new Event('session-expired'));
            return Promise.reject({ status: 401, message: 'Session expirée' });
        }
        if (response.status === 403) {
            return Promise.reject({ status: 403, message: 'Accès refusé' });
        }
        return Promise.reject(await response.json());
    } catch (error) {
        return Promise.reject(error);
    }
}

let refreshTokenPromise = null;

async function tryRefreshToken() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    // If another request is already refreshing the token, wait for it
    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }

    refreshTokenPromise = (async () => {
        const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? window.location.origin : 'https://sgrloto.com';
        try {
            const response = await fetch(`${API_BASE}/api/auth/refresh/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh: refreshToken }),
            });
            if (response.ok) {
                const data = await response.json();
                localStorage.setItem('access_token', data.access);
                if (data.refresh) localStorage.setItem('refresh_token', data.refresh);
                return true;
            }
        } catch (e) {
            console.error('Token refresh failed:', e);
        }
        // Refresh failed — clear tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return false;
    })();

    try {
        return await refreshTokenPromise;
    } finally {
        // Reset the promise when done, so future 401s can trigger a new refresh if needed
        refreshTokenPromise = null;
    }
}
